import React, { useEffect, useState } from "react";
import { ArrowLeft, ShoppingBag, AlertCircle, CheckCircle, Package } from "lucide-react";
import { FaTruck, FaCheckCircle as FaCheckCircleIcon, FaBox, FaTimesCircle, FaUndo } from 'react-icons/fa';
import { orderAPI } from "../../api";
import toast from "react-hot-toast";
import Swal from "sweetalert2";
import "../Orders/ViewOrders.css";

interface ViewReturnOrderProps {
  orderId: string; // This is the _id of the Order document
  productId: string;
  onBack: () => void;
}

export default function ViewReturnOrder({ orderId, productId, onBack }: ViewReturnOrderProps) {
  const [orderData, setOrderData] = useState<any>(null);
  const [product, setProduct] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const fetchOrderDetails = async () => {
    try {
      setLoading(true);
      const res = await orderAPI.getOrderDetails({ orderId });
      if (res.data.response === 'S') {
        const order = res.data.data;
        setOrderData(order);
        // /customer_order_details returns products wrapped in { product: {}, seller: {} }
        const wrappedProd = order.products.find((p: any) => p.product?.productId === productId);
        setProduct(wrappedProd ? wrappedProd.product : null);
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to load details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (orderId && productId) {
      fetchOrderDetails();
    }
  }, [orderId, productId]);

  const formatDate = (d: string) => {
    if (!d) return 'N/A';
    const date = new Date(d);
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })
      + ', '
      + date.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  const handleUpdateStatus = async (status: string) => {
    try {
      Swal.fire({
        title: 'Updating Status...',
        text: `Updating return status to ${status}...`,
        allowOutsideClick: false,
        allowEscapeKey: false,
        customClass: { popup: 'pickup-swal-popup' },
        didOpen: () => {
          Swal.showLoading();
        }
      });
      const res = await orderAPI.updateReturnStatus({
        orderId,
        productId,
        returnStatus: status
      });
      if (res.data.response === 'S') {
        await Swal.fire({
          icon: 'success',
          title: 'Status Updated',
          text: `Return marked as ${status} successfully!`,
          confirmButtonColor: '#0d9488',
          customClass: { popup: 'pickup-swal-popup' }
        });
        fetchOrderDetails();
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Update Failed',
          text: res.data.message || "Failed to update return status",
          confirmButtonColor: '#ef4444',
          customClass: { popup: 'pickup-swal-popup' }
        });
      }
    } catch (error: any) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || "Failed to update return status",
        confirmButtonColor: '#ef4444',
        customClass: { popup: 'pickup-swal-popup' }
      });
    }
  };

  const handleVerifyPickup = async () => {
    const { value: otp } = await Swal.fire({
      html: `
        <div style="padding: 4px 2px; font-family: 'Inter', sans-serif;">
          <div style="width: 62px; height: 62px; background: linear-gradient(135deg, #faf5ff, #f3e8ff); border: 2px solid #e9d5ff; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 16px; box-shadow: 0 8px 16px -4px rgba(147, 51, 234, 0.2);">
            <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#9333ea" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              <path d="m9 12 2 2 4-4"/>
            </svg>
          </div>
          <h2 style="font-size: 22px; font-weight: 800; color: #0f172a; margin: 0 0 6px 0; letter-spacing: -0.02em;">Verify Return Pickup</h2>
          <p style="font-size: 13.5px; color: #64748b; margin: 0 0 18px 0; line-height: 1.4;">Enter the OTP provided by the customer upon handover.</p>

          <div style="background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 12px 16px; margin-bottom: 16px; text-align: left;">
            <div style="font-size: 11px; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 4px;">Product</div>
            <div style="font-size: 13.5px; font-weight: 600; color: #1e293b; line-height: 1.4; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;" title="${product?.productName || ''}">
              ${product?.productName || ''}
            </div>
          </div>
        </div>
      `,
      input: 'text',
      inputPlaceholder: 'Enter 6-digit OTP',
      inputAttributes: {
        autocapitalize: 'off',
        style: 'text-align: center; font-size: 18px; letter-spacing: 4px; font-weight: 700; border-radius: 10px; border: 1.5px solid #cbd5e1; padding: 10px;'
      },
      showCancelButton: true,
      confirmButtonText: 'Verify OTP',
      cancelButtonText: 'Cancel',
      buttonsStyling: false,
      customClass: {
        popup: 'pickup-swal-popup',
        actions: 'pickup-swal-actions',
        confirmButton: 'pickup-swal-confirm-purple',
        cancelButton: 'pickup-swal-cancel',
        validationMessage: 'pickup-swal-validation'
      },
      inputValidator: (value) => {
        if (!value || !value.trim()) return 'Please enter the verification OTP';
      }
    });

    if (otp) {
      try {
        Swal.fire({
          title: 'Verifying...',
          text: 'Please wait while we verify the pickup OTP.',
          allowOutsideClick: false,
          allowEscapeKey: false,
          customClass: { popup: 'pickup-swal-popup' },
          didOpen: () => {
            Swal.showLoading();
          }
        });
        const res = await orderAPI.verifyReturnPickupOtp({ orderId, productId, otp: otp.trim() });
        if (res.data.response === 'S') {
          await Swal.fire({
            icon: 'success',
            title: 'Pickup Verified',
            text: 'Return pickup has been successfully verified!',
            confirmButtonColor: '#9333ea',
            customClass: { popup: 'pickup-swal-popup' }
          });
          fetchOrderDetails();
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Verification Failed',
            text: res.data.message || 'Invalid OTP',
            confirmButtonColor: '#ef4444',
            customClass: { popup: 'pickup-swal-popup' }
          });
        }
      } catch (err: any) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: err.response?.data?.message || 'Invalid OTP. Please try again.',
          confirmButtonColor: '#ef4444',
          customClass: { popup: 'pickup-swal-popup' }
        });
      }
    }
  };

  const getStatusClass = (status: string) => {
    if (!status) return "vo-badge vo-badge-gray";
    const s = status.toLowerCase();
    if (s === 'requested') return "vo-badge vo-badge-orange";
    if (s === 'approved') return "vo-badge vo-badge-blue";
    if (s === 'picked up') return "vo-badge vo-badge-purple";
    if (s === 'received') return "vo-badge vo-badge-green";
    return "vo-badge vo-badge-gray";
  };

  const returnStatus = product?.returnStatus || "Requested";
  const isRejected = returnStatus === "Rejected";

  const getStatusLevel = (status: string) => {
    switch (status) {
      case "Requested": return 1;
      case "Approved": return 2;
      case "Picked Up": return 3;
      case "Received": return 4;
      case "Refunded": return 4;
      case "Rejected": return 1;
      default: return 0;
    }
  };

  const level = getStatusLevel(returnStatus);

  const fullRefundAmount = product?.returnRefundAmount != null
    ? Number(product.returnRefundAmount)
    : (orderData?.totalAmount != null
        ? Number(orderData.totalAmount)
        : (product ? (product.discountPrice ? product.discountPrice * product.quantity : product.price * product.quantity) : 0));

  const getProgressWidth = () => {
    if (isRejected) return '0%';
    if (level >= 4) return '75%';
    if (level === 3) return '50%';
    if (level === 2) return '25%';
    return '0%';
  };

  const getReturnTimestamp = (status: string) => {
    if (status.toLowerCase() === 'requested' && (!product?.returnStatusHistory || !product.returnStatusHistory.some((h: any) => h.status?.toLowerCase() === 'requested'))) {
      const d = product?.returnRequestedAt || product?.updatedAt || orderData?.createdAt;
      if (d) {
        const dt = new Date(d);
        return `${dt.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}, ${dt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}`;
      }
    }
    if (!product?.returnStatusHistory) return null;
    const historyItem = product.returnStatusHistory.find((h: any) => h.status?.toLowerCase() === status.toLowerCase());
    if (!historyItem?.updatedAt) return null;
    const dt = new Date(historyItem.updatedAt);
    return `${dt.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}, ${dt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}`;
  };

  return (
    <div className="vo-page">
      {/* ── 1. HERO HEADER ── */}
      <div className="vo-hero">
        <div className="vo-hero-left">
          <button className="vo-back-btn" onClick={onBack}>
            <ArrowLeft size={14} />
            Back
          </button>
          <div className="vo-hero-title-wrap">
            <h2 className="vo-title">Return Order Details</h2>
            {orderData && (
              <span className="vo-order-id">#{orderData.orderId}</span>
            )}
          </div>
        </div>
        {product && (
          <div className="vo-hero-right">
            <span
              className={`vo-status-chip vo-chip-${product.returnStatus?.toLowerCase().replace(/\s+/g, '-') || 'default'}`}
              style={{ textTransform: 'capitalize' }}
            >
              {product.returnStatus || 'N/A'}
            </span>
          </div>
        )}
      </div>

      {/* ── Loading ── */}
      {loading && (
        <div className="vo-loading">
          <div className="vo-spinner" />
          Loading return details...
        </div>
      )}

      {!loading && orderData && product && (
        <div className="vo-body">
          <div className="vo-grid-2">
            {/* Return Request Summary */}
            <div className="vo-info-card">
              <div className="vo-info-card-header">
                <div className="vo-info-card-icon vo-icon-orange">
                  <AlertCircle size={17} />
                </div>
                <h3 className="vo-info-card-title">Return Summary</h3>
              </div>
              <div className="vo-info-card-body">
                <div className="vo-field">
                  <span className="vo-field-label">Product Name</span>
                  <span className="vo-field-value" title={product.productName}>{product.productName}</span>
                </div>
                <div className="vo-field">
                  <span className="vo-field-label">Quantity</span>
                  <span className="vo-field-value">{product.quantity}</span>
                </div>
                <div className="vo-field">
                  <span className="vo-field-label">Refund Amount</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <span className="vo-field-value" style={{ color: '#059669', fontSize: 18, fontWeight: 900 }}>
                      ₹{fullRefundAmount.toLocaleString('en-IN')}
                    </span>
                    <span style={{ fontSize: '11px', color: '#16a34a', background: '#dcfce7', border: '1px solid #bbf7d0', padding: '2px 8px', borderRadius: '6px', fontWeight: 700 }}>
                      Full Settlement
                    </span>
                  </div>
                </div>
                <div className="vo-field">
                  <span className="vo-field-label">Return Reason</span>
                  <span className="vo-field-value" style={{ color: '#ef4444', fontWeight: 600 }}>{product.returnReason || 'N/A'}</span>
                </div>
                <div className="vo-field">
                  <span className="vo-field-label">Status</span>
                  <span className={getStatusClass(product.returnStatus)}>
                    {product.returnStatus}
                  </span>
                </div>
              </div>
            </div>

            {/* Customer & Order Details */}
            <div className="vo-info-card">
              <div className="vo-info-card-header">
                <div className="vo-info-card-icon vo-icon-blue">
                  <ShoppingBag size={17} />
                </div>
                <h3 className="vo-info-card-title">Original Order Info</h3>
              </div>
              <div className="vo-info-card-body">
                <div className="vo-field">
                  <span className="vo-field-label">Order ID</span>
                  <span className="vo-field-value" style={{ fontFamily: 'monospace', fontWeight: 600 }}>{orderData.orderId}</span>
                </div>
                <div className="vo-field">
                  <span className="vo-field-label">Customer</span>
                  <span className="vo-field-value">{orderData.customerName || 'N/A'}</span>
                </div>
                <div className="vo-field">
                  <span className="vo-field-label">Mobile</span>
                  <span className="vo-field-value">{orderData.customerMobile || orderData.customerPhone || 'N/A'}</span>
                </div>
                <div className="vo-field">
                  <span className="vo-field-label">Order Date</span>
                  <span className="vo-field-value">{formatDate(orderData.createdAt)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Return Action Panel */}
          {product.returnStatus !== 'Received' ? (
            <div className="vo-info-card">
              <div className="vo-info-card-header">
                <div className="vo-info-card-icon vo-icon-purple">
                  <Package size={17} />
                </div>
                <h3 className="vo-info-card-title">Action Required</h3>
              </div>
              <div className="vo-info-card-body" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {product.returnStatus === 'Requested' && (
                  <button className="vo-btn-primary" onClick={() => handleUpdateStatus('Approved')}>
                    <CheckCircle size={16} /> Accept Return
                  </button>
                )}
                {product.returnStatus === 'Approved' && (
                  <button className="vo-btn-purple" onClick={handleVerifyPickup}>
                    <AlertCircle size={16} /> Verify Pickup (OTP)
                  </button>
                )}
                {product.returnStatus === 'Picked Up' && (
                  <button className="vo-btn-green" onClick={() => handleUpdateStatus('Received')}>
                    <CheckCircle size={16} /> Mark as Received
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div style={{ padding: '16px 20px', background: '#f0fdf4', color: '#15803d', borderRadius: '14px', border: '1px solid #bbf7d0', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '10px', fontSize: '14px' }}>
              <CheckCircle size={19} color="#16a34a" />
              Return successfully completed and product received at the shop.
            </div>
          )}

          {/* ── TRACK RETURN STATUS ── */}
          <div className="vo-track-card">
            <div className="vo-track-header">
              <div className="vo-track-icon">
                <FaUndo size={16} />
              </div>
              <h3>Track Return Status</h3>
            </div>

            <div className="vo-track-body">
              {isRejected ? (
                <div style={{ textAlign: 'center', padding: '36px 20px' }}>
                  <div style={{ width: 54, height: 54, borderRadius: '50%', background: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px', color: '#dc2626' }}>
                    <FaTimesCircle size={26} />
                  </div>
                  <h4 style={{ color: '#dc2626', margin: '0 0 6px', fontSize: 17, fontWeight: 800 }}>Return Request Rejected</h4>
                  <p style={{ color: '#64748b', margin: 0, fontSize: 13 }}>This return request has been rejected by the seller.</p>
                </div>
              ) : (
                <div className="track-stepper track-stepper-4">
                  {/* Progress bar */}
                  <div className="track-stepper-progress" style={{ width: getProgressWidth() }} />

                  {/* Step 1: Return Requested */}
                  <div className={`track-step ${level >= 1 ? 'completed' : ''}`}>
                    <div className="step-icon">
                      <FaUndo size={16} color={level >= 1 ? '#0d9488' : '#cbd5e1'} />
                    </div>
                    <div className="step-content">
                      <h4>Return Requested</h4>
                      <p>Customer has raised a return request</p>
                      {getReturnTimestamp('Requested') && (
                        <p className="step-time">{getReturnTimestamp('Requested')}</p>
                      )}
                    </div>
                  </div>

                  {/* Step 2: Return Approved */}
                  <div className={`track-step ${level >= 2 ? 'completed' : ''}`}>
                    <div className="step-icon">
                      <FaCheckCircleIcon size={17} color={level >= 2 ? '#0d9488' : '#cbd5e1'} />
                    </div>
                    <div className="step-content">
                      <h4>Return Approved</h4>
                      <p>Seller approved the return. OTP sent to customer.</p>
                      {getReturnTimestamp('Approved') && (
                        <p className="step-time">{getReturnTimestamp('Approved')}</p>
                      )}
                    </div>
                  </div>

                  {/* Step 3: Picked Up */}
                  <div className={`track-step ${level >= 3 ? 'completed' : ''}`}>
                    <div className="step-icon">
                      <FaTruck size={17} color={level >= 3 ? '#0d9488' : '#cbd5e1'} />
                    </div>
                    <div className="step-content">
                      <h4>Picked Up</h4>
                      <p>Delivery agent has picked up the item.</p>
                      {getReturnTimestamp('Picked Up') && (
                        <p className="step-time">{getReturnTimestamp('Picked Up')}</p>
                      )}
                    </div>
                  </div>

                  {/* Step 4: Received at Hub */}
                  <div className={`track-step ${level >= 4 ? 'completed' : ''}`}>
                    <div className="step-icon">
                      <FaBox size={17} color={level >= 4 ? '#0d9488' : '#cbd5e1'} />
                    </div>
                    <div className="step-content">
                      <h4>Received at Hub</h4>
                      <p>Product successfully returned and received.</p>
                      {getReturnTimestamp('Received') && (
                        <p className="step-time">{getReturnTimestamp('Received')}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
